import type { useUpdateUser } from "@hooks/trpc/useMe";

/**
 * Refer-a-friend links carry their intent in the query string
 * (`/?action=refer-friend&ref=<userId>`) rather than in Branch link metadata,
 * so a plain universal link is enough to deliver a referral.
 */
export const REFER_FRIEND_ACTION = "refer-friend";
const ACTION_PARAM = "action";
const REFERRER_PARAM = "ref";

export const buildReferFriendPath = (userId: string): string =>
  `/?${ACTION_PARAM}=${REFER_FRIEND_ACTION}&${REFERRER_PARAM}=${encodeURIComponent(
    userId,
  )}`;

/**
 * The referrer id carried by a refer-friend link, or `null` for any other
 * link. Deliberately parses the raw query string instead of `new URL`, so it
 * works the same for absolute URLs (`https://open.lissen.com/?…`, `lsn://…`)
 * and app-relative hrefs (`/?action=…`).
 */
export const readReferrerId = (url: string): string | null => {
  const queryStart = url.indexOf("?");
  if (queryStart === -1) return null;

  const query = url.slice(queryStart + 1).split("#")[0] ?? "";
  const params = new URLSearchParams(query);
  if (params.get(ACTION_PARAM) !== REFER_FRIEND_ACTION) return null;

  return params.get(REFERRER_PARAM) || null;
};

/**
 * Record who referred the signed-in user.
 *
 * Shared by both delivery paths: the Branch custom-action-link (old links
 * still in the wild) and the `?action=refer-friend&ref=…` universal link that
 * shares mint today.
 */
export const applyReferral = async ({
  referrerId,
  userId,
  updateUser,
}: {
  referrerId: string | undefined;
  userId: string;
  updateUser: ReturnType<typeof useUpdateUser>;
}): Promise<void> => {
  if (!referrerId) {
    console.log("::referral:: ignored, link carries no referrer");
    return;
  }

  if (referrerId === userId) {
    console.log("::referral:: ignored, cannot refer yourself");
    return;
  }

  await updateUser.mutateAsync({ referredBy: referrerId });
};
